import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { saveFreeReportForm } from '../firebase';

const BusinessReportForm = ({ onBack, onNavigateToBooking }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Section 1: Basic Info
    fullName: '',
    whatsappNumber: '',
    businessName: '',
    cityState: '',
    
    // Section 2: About Your Business
    whatYouSell: '',
    businessDuration: '',
    ticketSize: '',
    targetCustomers: [],
    
    // Section 3: Your Leads & Sales
    leadSources: [],
    monthlyLeads: '',
    conversions: '',
    leadIssue: '',
    
    // Section 4: Marketing & Tools
    runningAds: '',
    adBudget: '',
    trackROI: '',
    leadTools: [],
    
    // Section 5: Your Business Goals
    currentFocus: [],
    investmentReadiness: '',
    timeline: ''
  });
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [agreement, setAgreement] = useState(false);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // 3D Background Animation
  useEffect(() => {
    if (!canvasRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, alpha: true });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Create particle system
    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 2000;
    const posArray = new Float32Array(particlesCount * 3);

    for (let i = 0; i < particlesCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 10;
    }

    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));

    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.005,
      color: 0x00ffff,
      transparent: true,
      opacity: 0.6
    });

    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particlesMesh);

    // Create floating geometric shapes
    const geometry1 = new THREE.IcosahedronGeometry(0.5, 0);
    const material1 = new THREE.MeshBasicMaterial({
      color: 0x764ba2,
      wireframe: true,
      transparent: true,
      opacity: 0.3
    });
    const icosahedron = new THREE.Mesh(geometry1, material1);
    icosahedron.position.set(2, 2, -2);
    scene.add(icosahedron);

    const geometry2 = new THREE.OctahedronGeometry(0.4, 0);
    const material2 = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      wireframe: true,
      transparent: true,
      opacity: 0.3
    });
    const octahedron = new THREE.Mesh(geometry2, material2);
    octahedron.position.set(-2, -1, -3);
    scene.add(octahedron);

    camera.position.z = 3;

    // Animation loop
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);

      particlesMesh.rotation.x += 0.0005;
      particlesMesh.rotation.y += 0.0007;

      icosahedron.rotation.x += 0.01;
      icosahedron.rotation.y += 0.01;
      octahedron.rotation.x -= 0.008;
      octahedron.rotation.y -= 0.008;

      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      renderer.dispose();
      particlesGeometry.dispose();
      particlesMaterial.dispose();
      geometry1.dispose();
      material1.dispose();
      geometry2.dispose();
      material2.dispose();
    };
  }, []);

  const formSections = [
    {
      id: 'basic-info',
      title: 'Basic Info',
      icon: '🎯',
      color: 'linear-gradient(135deg, #ff6b6b, #ee5a52)'
    },
    {
      id: 'about-business',
      title: 'About Your Business',
      icon: '💼',
      color: 'linear-gradient(135deg, #4ecdc4, #45b7af)'
    },
    {
      id: 'leads-sales',
      title: 'Your Leads & Sales',
      icon: '📉',
      color: 'linear-gradient(135deg, #45b7d1, #4078c0)'
    },
    {
      id: 'marketing-tools',
      title: 'Marketing & Tools',
      icon: '📊',
      color: 'linear-gradient(135deg, #96ceb4, #85c1a3)'
    },
    {
      id: 'business-goals',
      title: 'Your Business Goals',
      icon: '🎯',
      color: 'linear-gradient(135deg, #ffeaa7, #fdcb6e)'
    }
  ];

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (name, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked 
        ? [...prev[name], value]
        : prev[name].filter(item => item !== value)
    }));
  };

  const handleAgreementChange = (checked) => {
    setAgreement(checked);
  };

  const validateCurrentStep = () => {
    switch(currentStep) {
      case 0:
        return formData.fullName && formData.whatsappNumber && formData.businessName && formData.cityState;
      case 1:
        return formData.whatYouSell && formData.businessDuration && formData.ticketSize && formData.targetCustomers.length > 0;
      case 2:
        return formData.leadSources.length > 0 && formData.monthlyLeads && formData.conversions && formData.leadIssue;
      case 3:
        return formData.runningAds && formData.adBudget && formData.trackROI && formData.leadTools.length > 0;
      case 4:
        return formData.currentFocus.length > 0 && formData.investmentReadiness && formData.timeline && agreement;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateCurrentStep() && currentStep < formSections.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    setIsSubmitting(true);

    try {
      // Save to Firebase
      const result = await saveFreeReportForm(formData);
      
      if (result.success) {
        console.log('Form saved to Firebase with ID:', result.id);
        setSubmissionId(result.id);
        setShowSuccess(true);
        
        // Optional: Send to backend as well for additional processing
        try {
          await fetch('http://localhost:5000/api/save-report-form', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...formData,
              firebaseId: result.id,
              submittedAt: new Date().toISOString()
            })
          });
        } catch (backendError) {
          console.log('Backend save failed, but Firebase save succeeded');
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again or contact us directly at +91-97999 18691');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatWhatsAppNumber = (value) => {
    let cleaned = value.replace(/\D/g, '');
    if (cleaned.startsWith('91')) {
      cleaned = cleaned.substring(2);
    }
    if (cleaned.length <= 10) {
      return '+91-' + cleaned;
    }
    return value;
  };

  if (showSuccess) {
    return (
      <div style={styles.container}>
        <canvas ref={canvasRef} style={styles.canvas} />
        <div style={styles.successContainer}>
          <div style={styles.successMessage}>
            <h2 style={styles.successTitle}>Amazing! Your Report is On Its Way! 🎉</h2>
            <p style={styles.successText}>
              We've received your business details and our AI + Human team is already working on your personalized lead generation strategy report.
            </p>

            <p style={styles.successText}>
              Your personalized business report will be delivered to your WhatsApp within 24 hours.
            </p>
            <p style={styles.nextStepText}>
              <strong>Ready to take the next step?</strong><br/>
              Book a FREE strategy session with our lead generation experts to discuss your custom solution.
            </p>
            
            <div style={styles.actionButtons}>
              <button 
                style={styles.bookingButton}
                onClick={() => {
                  if (onNavigateToBooking) {
                    // Pass the form data to the booking component
                    onNavigateToBooking(formData);
                  } else {
                    console.log('No navigation function provided');
                  }
                }}
              >
                📅 Book Your FREE Strategy Session
              </button>
              <button 
                style={styles.backButton}
                onClick={() => {
                  if (onBack) {
                    onBack();
                  } else {
                    console.log('No back navigation function provided');
                  }
                }}
              >
                ← Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderSection = () => {
    switch(currentStep) {
      case 0:
        return (
          <div style={styles.sectionContent}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Full Name *</label>
              <input
                style={styles.input}
                type="text"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>WhatsApp Number *</label>
              <input
                style={styles.input}
                type="tel"
                placeholder="+91-XXXXX XXXXX"
                value={formData.whatsappNumber}
                onChange={(e) => handleInputChange('whatsappNumber', formatWhatsAppNumber(e.target.value))}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Business Name *</label>
              <input
                style={styles.input}
                type="text"
                placeholder="Enter your business name"
                value={formData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>City/State *</label>
              <input
                style={styles.input}
                type="text"
                placeholder="e.g., Mumbai, Maharashtra"
                value={formData.cityState}
                onChange={(e) => handleInputChange('cityState', e.target.value)}
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div style={styles.sectionContent}>
            <div style={styles.formGroup}>
              <label style={styles.label}>What do you sell? *</label>
              <input
                style={styles.input}
                type="text"
                placeholder="Describe your products/services"
                value={formData.whatYouSell}
                onChange={(e) => handleInputChange('whatYouSell', e.target.value)}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>How long have you been running this business? *</label>
              <div style={styles.radioGroup}>
                {[
                  { value: 'less-than-6-months', label: 'Less than 6 months' },
                  { value: '6-12-months', label: '6–12 months' },
                  { value: '1-3-years', label: '1–3 years' },
                  { value: '3-plus-years', label: '3+ years' }
                ].map(option => (
                  <label key={option.value} style={styles.radioItem}>
                    <input
                      type="radio"
                      name="businessDuration"
                      value={option.value}
                      checked={formData.businessDuration === option.value}
                      onChange={(e) => handleInputChange('businessDuration', e.target.value)}
                      style={styles.radioInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>What's your average customer ticket size? *</label>
              <select
                style={styles.select}
                value={formData.ticketSize}
                onChange={(e) => handleInputChange('ticketSize', e.target.value)}
              >
                <option value="">Select ticket size</option>
                <option value="below-5000">Below ₹5,000</option>
                <option value="5000-20000">₹5,000–₹20,000</option>
                <option value="20000-50000">₹20,000–₹50,000</option>
                <option value="50000-plus">₹50,000+</option>
              </select>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Who do you mainly sell to? (Select all that apply) *</label>
              <div style={styles.checkboxGroup}>
                {[
                  { value: 'individuals-b2c', label: 'Individuals (B2C)' },
                  { value: 'small-businesses-b2b', label: 'Small Businesses (B2B)' },
                  { value: 'corporates', label: 'Corporates' },
                  { value: 'others', label: 'Others' }
                ].map(option => (
                  <label key={option.value} style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={formData.targetCustomers.includes(option.value)}
                      onChange={(e) => handleCheckboxChange('targetCustomers', option.value, e.target.checked)}
                      style={styles.checkboxInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div style={styles.sectionContent}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Where are your leads currently coming from? (Select all that apply) *</label>
              <div style={styles.checkboxGroup}>
                {[
                  { value: 'social-media', label: 'Social Media' },
                  { value: 'referrals', label: 'Referrals' },
                  { value: 'google-ads', label: 'Google Ads' },
                  { value: 'facebook-ads', label: 'Facebook Ads' },
                  { value: 'website', label: 'Website' },
                  { value: 'cold-outreach', label: 'Cold Outreach' }
                ].map(option => (
                  <label key={option.value} style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={formData.leadSources.includes(option.value)}
                      onChange={(e) => handleCheckboxChange('leadSources', option.value, e.target.checked)}
                      style={styles.checkboxInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>How many leads do you get monthly? *</label>
              <select
                style={styles.select}
                value={formData.monthlyLeads}
                onChange={(e) => handleInputChange('monthlyLeads', e.target.value)}
              >
                <option value="">Select monthly leads</option>
                <option value="less-than-10">Less than 10</option>
                <option value="10-30">10–30</option>
                <option value="30-100">30–100</option>
                <option value="100-plus">100+</option>
              </select>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>How many convert to paying customers? *</label>
              <select
                style={styles.select}
                value={formData.conversions}
                onChange={(e) => handleInputChange('conversions', e.target.value)}
              >
                <option value="">Select conversions</option>
                <option value="0">0</option>
                <option value="1-5">1–5</option>
                <option value="6-10">6–10</option>
                <option value="10-plus">10+</option>
              </select>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Biggest lead quality issue? *</label>
              <div style={styles.radioGroup}>
                {[
                  { value: 'not-interested', label: 'Not interested' },
                  { value: 'fake-numbers', label: 'Fake numbers' },
                  { value: 'no-budget', label: 'No budget' },
                  { value: 'dont-respond', label: "Don't respond" },
                  { value: 'leads-are-fine', label: 'Leads are fine' }
                ].map(option => (
                  <label key={option.value} style={styles.radioItem}>
                    <input
                      type="radio"
                      name="leadIssue"
                      value={option.value}
                      checked={formData.leadIssue === option.value}
                      onChange={(e) => handleInputChange('leadIssue', e.target.value)}
                      style={styles.radioInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div style={styles.sectionContent}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Are you currently running ads? *</label>
              <div style={styles.radioGroup}>
                {[
                  { value: 'meta-ads', label: 'Meta Ads' },
                  { value: 'google-ads', label: 'Google Ads' },
                  { value: 'not-yet', label: 'Not yet' },
                  { value: 'tried-but-failed', label: 'Tried but failed' }
                ].map(option => (
                  <label key={option.value} style={styles.radioItem}>
                    <input
                      type="radio"
                      name="runningAds"
                      value={option.value}
                      checked={formData.runningAds === option.value}
                      onChange={(e) => handleInputChange('runningAds', e.target.value)}
                      style={styles.radioInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Monthly ad budget? *</label>
              <select
                style={styles.select}
                value={formData.adBudget}
                onChange={(e) => handleInputChange('adBudget', e.target.value)}
              >
                <option value="">Select budget</option>
                <option value="0">₹0</option>
                <option value="1000-5000">₹1,000–₹5,000</option>
                <option value="5000-20000">₹5,000–₹20,000</option>
                <option value="20000-plus">₹20,000+</option>
              </select>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Do you track your ROI? *</label>
              <div style={styles.radioGroup}>
                {[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' }
                ].map(option => (
                  <label key={option.value} style={styles.radioItem}>
                    <input
                      type="radio"
                      name="trackROI"
                      value={option.value}
                      checked={formData.trackROI === option.value}
                      onChange={(e) => handleInputChange('trackROI', e.target.value)}
                      style={styles.radioInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Do you use any tools to manage leads? (Select all that apply) *</label>
              <div style={styles.checkboxGroup}>
                {[
                  { value: 'google-sheets', label: 'Google Sheets' },
                  { value: 'crm', label: 'CRM' },
                  { value: 'whatsapp-only', label: 'WhatsApp only' },
                  { value: 'none', label: 'None' }
                ].map(option => (
                  <label key={option.value} style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={formData.leadTools.includes(option.value)}
                      onChange={(e) => handleCheckboxChange('leadTools', option.value, e.target.checked)}
                      style={styles.checkboxInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div style={styles.sectionContent}>
            <div style={styles.formGroup}>
              <label style={styles.label}>What's your current focus? (Select all that apply) *</label>
              <div style={styles.checkboxGroup}>
                {[
                  { value: 'get-more-leads', label: 'Get more leads' },
                  { value: 'convert-more-sales', label: 'Convert more sales' },
                  { value: 'build-automation', label: 'Build automation' },
                  { value: 'improve-branding', label: 'Improve branding' }
                ].map(option => (
                  <label key={option.value} style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={formData.currentFocus.includes(option.value)}
                      onChange={(e) => handleCheckboxChange('currentFocus', option.value, e.target.checked)}
                      style={styles.checkboxInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Are you open to investing in growth after this session? *</label>
              <div style={styles.radioGroup}>
                {[
                  { value: 'yes-if-helps', label: 'Yes, if it helps' },
                  { value: 'maybe', label: 'Maybe' },
                  { value: 'not-right-now', label: 'Not right now' }
                ].map(option => (
                  <label key={option.value} style={styles.radioItem}>
                    <input
                      type="radio"
                      name="investmentReadiness"
                      value={option.value}
                      checked={formData.investmentReadiness === option.value}
                      onChange={(e) => handleInputChange('investmentReadiness', e.target.value)}
                      style={styles.radioInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>How fast do you want to improve this? *</label>
              <div style={styles.radioGroup}>
                {[
                  { value: 'immediately', label: 'Immediately' },
                  { value: 'within-2-weeks', label: 'Within 2 weeks' },
                  { value: '1-month', label: '1 month' },
                  { value: 'just-exploring', label: 'Just exploring' }
                ].map(option => (
                  <label key={option.value} style={styles.radioItem}>
                    <input
                      type="radio"
                      name="timeline"
                      value={option.value}
                      checked={formData.timeline === option.value}
                      onChange={(e) => handleInputChange('timeline', e.target.value)}
                      style={styles.radioInput}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Agreement Checkbox - Only in last section */}
            <div style={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={agreement}
                onChange={(e) => handleAgreementChange(e.target.checked)}
                style={styles.checkboxInput}
              />
              By clicking submit, I agree to the terms & conditions and privacy policy and give my consent to receive updates through SMS/Email.
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / formSections.length) * 100;

  return (
    <div style={styles.container}>
      <canvas ref={canvasRef} style={styles.canvas} />
      
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logo}>
          <div style={styles.logoIcon}>LV</div>
          <span>LEADVALA</span>
        </div>
        <button style={styles.backButton} onClick={() => {
          if (onBack) {
            onBack();
          } else {
            console.log('No back navigation function provided');
          }
        }}>
          ← Back to Home
        </button>
      </div>

      {/* Progress Bar */}
      <div style={styles.progressContainer}>
        <div style={{...styles.progressBar, width: `${progress}%`}}></div>
      </div>

      {/* Form Container */}
      <div style={styles.formContainer}>
        <div style={styles.formHeader}>
          <h1 style={styles.formTitle}>Get Your Business Report</h1>
          <p style={styles.formSubtitle}>
            Step {currentStep + 1} of {formSections.length}: {formSections[currentStep].title}
          </p>
        </div>

        <div style={styles.formSection}>
          <div style={styles.sectionHeader}>
            <div style={{...styles.sectionIcon, background: formSections[currentStep].color}}>
              {formSections[currentStep].icon}
            </div>
            <h2 style={styles.sectionTitle}>{formSections[currentStep].title}</h2>
          </div>

          {renderSection()}

          <div style={styles.buttonContainer}>
            {currentStep > 0 && (
              <button style={styles.prevButton} onClick={prevStep}>
                ← Previous
              </button>
            )}
            
            {currentStep < formSections.length - 1 ? (
              <button 
                style={{
                  ...styles.nextButton,
                  opacity: validateCurrentStep() ? 1 : 0.5,
                  cursor: validateCurrentStep() ? 'pointer' : 'not-allowed'
                }}
                onClick={nextStep}
                disabled={!validateCurrentStep()}
              >
                Next →
              </button>
            ) : (
              <button 
                style={{
                  ...styles.submitButton,
                  opacity: validateCurrentStep() && !isSubmitting ? 1 : 0.5,
                  cursor: validateCurrentStep() && !isSubmitting ? 'pointer' : 'not-allowed'
                }}
                onClick={handleSubmit}
                disabled={!validateCurrentStep() || isSubmitting}
              >
                {isSubmitting ? '⏳ Saving...' : '🚀 Get My Business Report'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    color: 'white',
    fontFamily: 'Arial, sans-serif',
    position: 'relative',
    overflow: 'hidden'
  },
  canvas: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: -1
  },
  header: {
    padding: '20px 50px',
    background: 'rgba(26, 26, 46, 0.95)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#00ffff'
  },
  logoIcon: {
    width: '40px',
    height: '40px',
    marginRight: '10px',
    background: 'linear-gradient(135deg, #00ffff, #764ba2)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    color: 'white'
  },
  backButton: {
    background: 'transparent',
    color: '#00ffff',
    border: '2px solid #00ffff',
    padding: '10px 20px',
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  progressContainer: {
    height: '4px',
    background: 'rgba(255, 255, 255, 0.1)',
    margin: '0'
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #00ffff, #764ba2)',
    transition: 'width 0.3s ease'
  },
  formContainer: {
    maxWidth: '800px',
    margin: '50px auto',
    padding: '0 20px',
    position: 'relative',
    zIndex: 1
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: '40px'
  },
  formTitle: {
    fontSize: '3rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    background: 'linear-gradient(135deg, #00ffff, #764ba2)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  formSubtitle: {
    fontSize: '1.2rem',
    opacity: 0.9
  },
  formSection: {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '40px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    minHeight: '500px'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '30px'
  },
  sectionIcon: {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '20px',
    fontSize: '24px'
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    margin: 0
  },
  sectionContent: {
    marginBottom: '40px'
  },
  formGroup: {
    marginBottom: '25px'
  },
  label: {
    display: 'block',
    marginBottom: '10px',
    fontWeight: 'bold',
    color: '#ffffff',
    fontSize: '1.1rem'
  },
  input: {
    width: '100%',
    padding: '15px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '15px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    outline: 'none'
  },
  radioGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginTop: '10px'
  },
  radioItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '15px',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  radioInput: {
    marginRight: '12px',
    width: '18px',
    height: '18px',
    accentColor: '#00ffff'
  },
  checkboxGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '12px',
    marginTop: '10px'
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '15px',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  checkboxInput: {
    marginRight: '12px',
    width: '18px',
    height: '18px',
    accentColor: '#00ffff'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '40px'
  },
  prevButton: {
    background: 'transparent',
    color: '#00ffff',
    border: '2px solid #00ffff',
    padding: '15px 30px',
    borderRadius: '25px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  nextButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '15px 30px',
    border: 'none',
    borderRadius: '25px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginLeft: 'auto'
  },
  submitButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '18px 40px',
    border: 'none',
    borderRadius: '30px',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginLeft: 'auto',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
  },
  successContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px'
  },
  successMessage: {
    textAlign: 'center',
    padding: '60px 40px',
    background: 'rgba(76, 175, 80, 0.2)',
    border: '2px solid #4caf50',
    borderRadius: '20px',
    maxWidth: '600px',
    backdropFilter: 'blur(10px)'
  },
  successTitle: {
    fontSize: '2.5rem',
    marginBottom: '20px',
    color: '#4caf50'
  },
  successText: {
    fontSize: '1.2rem',
    marginBottom: '15px',
    opacity: 0.9
  },
  submissionInfo: {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    padding: '20px',
    margin: '20px 0',
    textAlign: 'left'
  },
  nextStepText: {
    fontSize: '1.2rem',
    marginBottom: '25px',
    opacity: 0.9,
    textAlign: 'center',
    background: 'rgba(0, 255, 255, 0.1)',
    borderRadius: '10px',
    padding: '20px',
    border: '1px solid rgba(0, 255, 255, 0.3)'
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    alignItems: 'center',
    margin: '30px 0'
  },
  bookingButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    padding: '18px 35px',
    borderRadius: '30px',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
    minWidth: '320px'
  }
};

export default BusinessReportForm;